import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { AdminLinkButton } from "@/admin/AdminLinkButton.tsx";
import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { TextFilter } from "@/admin/filters.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import {
    type GetServicePackageListQuery,
    OrderDirection,
    ServicePackageOrderFields,
    VmOrderFields,
} from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { buildTableExactSearch } from "@/admin/itemLinks.ts";
import { ServiceOs } from "@/admin/services/ServiceOs.tsx";
import { ServiceStatusBadge, ServiceStatusFilter } from "@/admin/services/ServiceStatus.tsx";
import {
    baField,
    baIdField,
    createdAtField,
    locationField,
    userIdField,
} from "@/admin/table_fields/standardFields.tsx";
import { link } from "@/components/Action.tsx";
import { plural } from "@/utils/plural.tsx";
import { useQuery } from "@apollo/client/react";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { ServiceStatusEnum } from "@warrenio/api-spec/spec.oats.gen";

const GET_SERVICEPACKAGE_LIST = gql(/* GraphQL */ `
    query GetServicePackageList(
        $limit: Int
        $page: Int
        $orderField: ServicePackageOrderFields
        $orderDir: OrderDirection
        $search: String
        $locations: [String!]
        $filters: [ServicePackageFilter!]
    ) {
        admin_servicepackage_list(
            limit: $limit
            page: $page
            orderField: $orderField
            orderDir: $orderDir
            search: $search
            filters: $filters
            locations: $locations
        ) {
            items {
                uuid
                user_id
                display_name
                billing_account_id
                is_multi_node
                service
                status
                version
                created_at
                updated_at
                is_deleted
                resources {
                    resource_id
                    resource_location
                    resource_type
                }
                properties {
                    service_ip
                }
                ip_address_object {
                    address
                }
                whitelist_addresses
                billing_account {
                    ...BillingAccountBlock
                }
                _location
            }
            paging {
                total
            }
        }
    }
`);

export type GQServicePackageItem = NonNullable<
    GetServicePackageListQuery["admin_servicepackage_list"]["items"]
>[number];

function VmsCount({ item }: { item: GQServicePackageItem }) {
    const vms = item.resources?.filter((vm) => vm.resource_type === "vm") ?? [];

    return (
        <AdminLinkButton
            action={link({
                to: "/admin/virtual_machines",
                search: buildTableExactSearch(VmOrderFields.ServiceUuid, item.uuid),
            })}
            label={plural(vms.length, "VM", "VMs")}
        />
    );
}

const ServicePackageFields: GqlFieldsOf<GQServicePackageItem, ServicePackageOrderFields> = [
    gf({
        ...userIdField,
        get: (a) => a.user_id,
        order: ServicePackageOrderFields.UserId,
    }),
    gf({
        ...baIdField,
        get: (a) => a.billing_account_id,
        order: ServicePackageOrderFields.BillingAccountId,
    }),
    gf({
        ...baField,
        get: (a) => a.billing_account,
        render: BillingAccountBlock,
    }),
    gf({
        id: "uuid",
        title: "UUID",
        get: (a) => a.uuid,
        copyable: true,
        order: ServicePackageOrderFields.Uuid,
        filter: TextFilter,
    }),
    gf({
        ...createdAtField,
        get: (a) => (a.created_at ? apiUnixToDate(a.created_at as number) : undefined),
        order: ServicePackageOrderFields.CreatedAt,
    }),
    gf({
        id: "service",
        title: "Service",
        get: (a) => a.service,
        render: ServiceOs,
        order: ServicePackageOrderFields.Service,
        filter: TextFilter,
    }),
    gf({
        id: "display_name",
        title: "Service Name",
        get: (a) => a.display_name,
        order: ServicePackageOrderFields.DisplayName,
        filter: TextFilter,
    }),
    gf({
        id: "address",
        title: "Public IP",
        get: (a) => a.ip_address_object?.address,
        copyable: true,
    }),
    gf({
        id: "service_ip",
        title: "Service IP",
        get: (a) => a.properties?.service_ip,
        copyable: true,
    }),
    gf({
        id: "whitelist_addresses",
        title: "Whitelist\nAddresses",
        get: (a) => a.whitelist_addresses,
        render: ({ value }) => <div className="max-w-300px text-ellipsis overflow-x-hidden">{value?.join(", ")}</div>,
        hidden: true,
    }),
    gf({
        id: "vms",
        title: "VMs",
        get: (a) => a,
        render: VmsCount,
    }),
    gf({
        ...locationField,
        get: (a) => a._location,
    }),
    gf({
        id: "status",
        title: "Status",
        get: (a) => a.status as ServiceStatusEnum,
        order: ServicePackageOrderFields.Status,
        render: ServiceStatusBadge,
        filter: ServiceStatusFilter,
    }),
];

export function ServicePackagesTable() {
    return (
        <GraphqlTable
            title="Service Packages"
            fields={ServicePackageFields}
            defaults={{ orderField: ServicePackageOrderFields.CreatedAt, orderDir: OrderDirection.Desc }}
            getId={(item) => item.uuid}
            hasLocations={false}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_SERVICEPACKAGE_LIST, { variables });
                return extractData(q, (d) => d.admin_servicepackage_list);
            }}
        />
    );
}
